import * as React from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SubscriptionRow } from './index';
import { getCurrentSubscription } from '../../../services/webPush';
import { formatRelativeTime } from '../../../utils/relativeTime';

interface Props {
    subscriptions: SubscriptionRow[];
    onRemove: (subscriptionId: number) => void | Promise<void>;
}

/**
 * Lists every push subscription the current user has registered. Each row
 * shows device label, last seen, and a Remove button — except the row for
 * the device the user is currently on, which is highlighted as "This device"
 * and gets confirmation before removal (taking it away on this device
 * silently would be confusing).
 *
 * Matching "this device" is done by endpoint URL: the browser's local
 * PushSubscription has the same endpoint as the one we POSTed to the backend.
 */
const RegisteredDevicesSection: React.FC<Props> = ({ subscriptions, onRemove }) => {
    const intl = useIntl();
    const [localEndpoint, setLocalEndpoint] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            const sub = await getCurrentSubscription();
            if (sub) setLocalEndpoint(sub.endpoint);
        })();
    }, []);

    const handleRemove = (sub: SubscriptionRow) => {
        const isThisDevice = sub.endpoint === localEndpoint;
        if (isThisDevice) {
            const ok = window.confirm(intl.formatMessage({
                id: 'notifications.settings.devices.confirmRemoveSelf',
                defaultMessage: 'Remove browser push from THIS device? You will stop receiving notifications here.',
            }));
            if (!ok) return;
        }
        onRemove(sub.id);
    };

    return (
        <section className="notif-settings__section notif-settings__section--devices">
            <header className="notif-settings__section-head">
                <span className="notif-settings__section-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                </span>
                <div className="notif-settings__section-head-text">
                    <h2 className="notif-settings__section-title">
                        <FormattedMessage id="notifications.settings.devices.title" defaultMessage="Registered devices" />
                    </h2>
                    <p className="notif-settings__section-desc">
                        <FormattedMessage
                            id="notifications.settings.devices.desc"
                            defaultMessage="Devices that receive browser push notifications. Removing a device stops push to it; in-app notifications still work everywhere."
                        />
                    </p>
                </div>
                <span className={`notif-settings__pill ${subscriptions.length > 0 ? 'notif-settings__pill--devices-active' : 'notif-settings__pill--off'}`}>
                    <FormattedMessage
                        id="notifications.settings.devices.pillCount"
                        defaultMessage="{count, plural, =0 {0 PAJISJE} one {# PAJISJE} other {# PAJISJE}}"
                        values={{ count: subscriptions.length }}
                    />
                </span>
            </header>

            {subscriptions.length === 0 && (
                <div className="notif-settings__empty">
                    <FormattedMessage
                        id="notifications.settings.devices.empty"
                        defaultMessage="No devices registered yet. Enable browser notifications from the inbox to add this one."
                    />
                </div>
            )}

            {subscriptions.length > 0 && (
                <ul className="notif-settings__list">
                    {subscriptions.map((sub) => {
                        const isThisDevice = sub.endpoint === localEndpoint;
                        // Replaces moment.fromNow() — moment locales aren't loaded
                        // anywhere in the app so .locale('sq').fromNow() silently
                        // returned English. Intl.RelativeTimeFormat is built-in and
                        // locale-aware out of the box.
                        const added = formatRelativeTime(intl.locale, sub.created_at);
                        return (
                            <li key={sub.id} className={`notif-settings__list-row ${isThisDevice ? 'is-current' : ''}`}>
                                <div className="notif-settings__list-main">
                                    <span className="notif-settings__list-label">
                                        {sub.device_label || sub.user_agent || 'Unknown device'}
                                        {isThisDevice && (
                                            <span className="notif-settings__current-badge">
                                                <FormattedMessage id="notifications.settings.devices.thisDevice" defaultMessage="This device" />
                                            </span>
                                        )}
                                    </span>
                                    {added && (
                                        <span className="notif-settings__list-meta">
                                            <FormattedMessage
                                                id="notifications.settings.devices.added"
                                                defaultMessage="Added {when}"
                                                values={{ when: added }}
                                            />
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="notif-settings__list-action notif-settings__list-action--danger"
                                    onClick={() => handleRemove(sub)}
                                    aria-label={intl.formatMessage({
                                        id: 'notifications.settings.devices.remove',
                                        defaultMessage: 'Remove',
                                    })}
                                >
                                    {/* X icon — text is hidden on mobile via
                                        SCSS so the row stays narrow. aria-label
                                        carries the full string for screen readers. */}
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="notif-settings__list-action-icon"
                                        aria-hidden="true"
                                    >
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                    <span className="notif-settings__list-action-text">
                                        <FormattedMessage id="notifications.settings.devices.remove" defaultMessage="Remove" />
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};

export default RegisteredDevicesSection;
